/**
 * @file
 * Icon and data processing for BasstrakkMap.
 */
import leaflet, { LatLngBounds, LatLngExpression } from 'leaflet';
import isEqual from 'lodash/isEqual';

// Types
import {
  AnglerId,
  CatchId,
  IBasstrakkAngler,
  IBasstrakkCatch,
} from '../../types';
import {
  IBaseData,
  ICatch,
  IconType,
  ILocation,
  IMarker,
  IMarkerPosition,
  LayerType,
} from './types';

// Markers
import { markerList } from './markers';

// Constants
// Max rank for 'top 12' anglers / catches.
const MAX_RANK = 12;
// A minor variance to add to duplicate lat/lng coordinates.
const COORDINATE_VARIANCE = 0.2;

/**
 * Returns the first 12 elements of locations data.
 */
export const getTop12Anglers = (data: ILocation[]) => data.slice(0, MAX_RANK);

/**
 * Returns the first element to max rank elements of catches data.
 */
export const getTop12Catches = (data: ICatch[]) =>
  data.filter(({ rank }) => rank <= MAX_RANK);

/**
 * Accepts an id and an array of catches or locations data.
 * Returns an array of all objects with matching ids.
 */
export const getDataById = (idToMatch: AnglerId | CatchId, data: IBaseData[]) =>
  data.filter(entry => entry.anglerId === idToMatch);

/**
 * Filters and builds an array of formatted location objects from raw API data.
 */
export const processLocationData = (
  basstrakkAnglers: IBasstrakkAngler[],
  defaultLat: number,
  defaultLng: number
): ILocation[] =>
  basstrakkAnglers.map(
    ({
      FISHERMAN_PHONENAME,
      FISHERMENID,
      LastLatitude,
      LastLocationTime,
      LastLongitude,
      Rank,
      SumFishCount,
      SumPdsOz,
    }) => ({
      anglerId: FISHERMENID,
      anglerName: FISHERMAN_PHONENAME,
      lastLocationTime: LastLocationTime,
      markerId: FISHERMENID.toString(),
      // Check for invalid lat / lng and set to default as necessary.
      position: isValidLatLng(LastLatitude, LastLongitude)
        ? ([
            parseFloat(LastLatitude),
            parseFloat(LastLongitude),
          ] as IMarkerPosition)
        : ([defaultLat, defaultLng] as IMarkerPosition),
      rank: Rank,
      sumFish: SumFishCount,
      sumPdsOz: SumPdsOz,
    })
  );

/**
 * Filters and builds an array of formatted catches objects from raw API data.
 */
export const processCatchesData = (
  basstrakkCatches: IBasstrakkCatch[],
  locationData: ILocation[],
  defaultLat: number,
  defaultLng: number
): ICatch[] =>
  basstrakkCatches.map(
    ({
      AnglerId: basstrakkAnglerId,
      uid,
      FishWeightOz,
      FishWeightPdsOz,
      Latitude,
      Longitude,
      Time,
    }) => ({
      anglerId: basstrakkAnglerId,
      fishWeightOz: FishWeightOz,
      fishWeightPdsOz: FishWeightPdsOz,
      markerId: uid,
      // Check for invalid lat / lng and set to default as necessary.
      position: isValidLatLng(Latitude, Longitude)
        ? ([parseFloat(Latitude), parseFloat(Longitude)] as IMarkerPosition)
        : ([defaultLat, defaultLng] as IMarkerPosition),
      rank: getAnglerById(basstrakkAnglerId, locationData).rank,
      time: Time,
    })
  );

/**
 * Various checks to filter out invalid lat/lng from basstrakk data.
 */
export const isValidLatLng = (lat: string, lng: string) =>
  !!parseFloat(lat) && !!parseFloat(lng);

/**
 * Retrieve angler name from location data.
 */
export const getAnglerById = (id: AnglerId, locationData: ILocation[]) => {
  const match = locationData.find(({ anglerId }) => anglerId === id);

  return match ? match : { anglerName: '', rank: 0 };
};

export const getAnglerOptionTestId = (anglerName: string, anglerId: AnglerId) =>
  `${anglerName.split(' ').join('-').toLowerCase()}-${anglerId}`;

/**
 * Creates custom fish or boat icons for BasstrakkMap based on layer type and
 * angler rank.
 * @param layerType: 'locations' or 'catches'.
 * @param rank: A numeric value derived from angler rank.
 */
export const createCustomIcon = (
  layerType: LayerType,
  rank: number
): leaflet.Icon => {
  // If rank is greater than max value, just use max value since there are only
  // 12 pngs for catches and locations.
  const imageNumber = rank > MAX_RANK || rank === 0 ? MAX_RANK : rank;

  return layerType === LayerType.LOCATIONS
    ? // If layer is locations, create boat icons.
      leaflet.icon({
        iconAnchor: [28, 20],
        iconSize: [42, 15],
        iconUrl: markerList[IconType.BOAT][imageNumber],
        popupAnchor: [-7, -16],
      })
    : // If layer is catches, create fish icons.
      leaflet.icon({
        iconAnchor: [28, 16],
        iconSize: [38, 20],
        iconUrl: markerList[IconType.FISH][imageNumber],
        popupAnchor: [-7, -16],
      });
};

/**
 * Returns a bounds object for given marker objects so that Leaflet
 * automatically adjust map zoom and centering as filters are clicked.
 */
export const getMapBounds = (markerData: IMarker[]): LatLngBounds => {
  const bounds = markerData.reduce(
    (acc, marker) => {
      const [lat, lng] = marker.position;
      // Min lat
      if (lat < acc.min[0]) acc.min[0] = lat;
      // Min lng
      if (lng < acc.min[1]) acc.min[1] = lng;
      // Max lat
      if (lat > acc.max[0]) acc.max[0] = lat;
      // Max lng
      if (lng > acc.max[1]) acc.max[1] = lng;
      return acc;
    },
    {
      max: [...markerData[0].position],
      min: [...markerData[0].position],
    } as any
  );
  const latLngBounds = new leaflet.LatLngBounds(bounds.min, bounds.max);

  // If map bounds are equal, get the fallback of the new bounds, otherwise use
  // the new bounds.
  return mapBoundsAreEqual(latLngBounds)
    ? // bounds.min[0] = lat, bounds.min[1] = lng
      getFallbackBounds(bounds.min[0], bounds.min[1])
    : latLngBounds;
};

/**
 * Determines if map bounds are equal.
 */
export const mapBoundsAreEqual = (bounds: LatLngBounds) =>
  isEqual(bounds.getNorthEast(), bounds.getSouthWest());

/**
 * Adds some cushion to default lat and lng props to create coordinates that are
 * unique map bounds.
 */
export const getFallbackBounds = (lat: number, lng: number) => {
  return new leaflet.LatLngBounds(
    [lat - COORDINATE_VARIANCE, lng - COORDINATE_VARIANCE] as LatLngExpression,
    [lat + COORDINATE_VARIANCE, lng + COORDINATE_VARIANCE] as LatLngExpression
  );
};
